// AiChat.jsx
import React, { useState, useEffect, useRef } from 'react';
import { __ } from '@wordpress/i18n';
import AskQuestion from './components/AskQuestion';
import ExampleQuestions from './components/ExampleQuestions';
import AiCredits from '../../AiCredits';

/**
 * AiChat - Tailwind version with typing/loading indicator
 * Paste into your project. Requires EASYCOMMERCE.rest_base and Tailwind.
 */

const ErrorMessage = ({ text }) => {
	const [show, setShow] = React.useState(false);

	React.useEffect(() => {
		const timeout = setTimeout(() => setShow(true), 50);
		return () => clearTimeout(timeout);
	}, []);

	return (
		<div className={`flex flex-col gap-2 mb-[32px] group transition-all duration-500 ease-out
			${show ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-3'}`}
		>
			<div className="text-sm rounded-[10px] py-2.5 px-5 border border-red-700 text-red-800">
				<span>{'\u26A0'}</span> {text}
			</div>
		</div>
	);
};

const AiChat = ({
	user,
	isBotTyping,
	setIsBotTyping,
	pendingBotMessage,
	setPendingBotMessage,
	messages,
	setMessages,
}) => {
	const [input, setInput] = useState('');
	const [isFocused, setIsFocused] = useState(false);
	const [hasSentMessage, setHasSentMessage] = useState(false);
	const inputRef = useRef(null);
	const chatContainerRef = useRef(null);
	const chatEndRef = useRef(null);

	const isLicensed = EASYCOMMERCE.pro.activated && EASYCOMMERCE.pro.licensed;
	const usingPersonalAIProvider = EASYCOMMERCE.ai_provider !== 'easycommerce';

	const scrollToBottom = () => {
		if (!chatContainerRef.current) return;

		chatEndRef.current?.scrollIntoView({
			behavior: 'smooth',
			block: 'end',
		});
	};

	useEffect(() => {
		scrollToBottom();
	}, [messages, isBotTyping]);

	useEffect(() => {
		if (!isBotTyping) {
			setTimeout(() => {
				inputRef.current?.focus();
			}, 0);
		}
	}, []);


	const sendIcon = (
		<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
			<g clip-path="url(#clip0_6976_6158)">
			<path fill-rule="evenodd" clip-rule="evenodd" d="M24 12C24 5.3832 18.6168 0 12 0C5.3832 0 0 5.3832 0 12C0 18.6168 5.3832 24 12 24C18.6168 24 24 18.6168 24 12ZM13.9032 10.8L12.3516 9.2484C12.133 9.02208 12.0121 8.71896 12.0148 8.40432C12.0175 8.08968 12.1437 7.78871 12.3662 7.56622C12.5887 7.34373 12.8897 7.21753 13.2043 7.21479C13.519 7.21206 13.8221 7.33301 14.0484 7.5516L17.6484 11.1516C17.7602 11.2628 17.849 11.395 17.9095 11.5406C17.9701 11.6862 18.0012 11.8423 18.0012 12C18.0012 12.1577 17.9701 12.3138 17.9095 12.4594C17.849 12.605 17.7602 12.7372 17.6484 12.8484L14.0484 16.4484C13.8221 16.667 13.519 16.7879 13.2043 16.7852C12.8897 16.7825 12.5887 16.6563 12.3662 16.4338C12.1437 16.2113 12.0175 15.9103 12.0148 15.5957C12.0121 15.281 12.133 14.9779 12.3516 14.7516L13.9032 13.2H7.2C6.88174 13.2 6.57652 13.0736 6.35147 12.8485C6.12643 12.6235 6 12.3183 6 12C6 11.6817 6.12643 11.3765 6.35147 11.1515C6.57652 10.9264 6.88174 10.8 7.2 10.8H13.9032Z" fill="#000"/>
			</g>
			<defs>
			<clipPath id="clip0_6976_6158">
			<rect width="24" height="24" fill="white" transform="matrix(-1 0 0 1 24 0)"/>
			</clipPath>
			</defs>
		</svg>
	);

	const searchIcon = (
		<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path d="M9.97031 5.577L10.5726 7.2495C10.903 8.16639 11.4317 8.99908 12.1208 9.68824C12.81 10.3774 13.6427 10.906 14.5596 11.2365L16.2321 11.8388C16.2651 11.8509 16.2936 11.8728 16.3137 11.9017C16.3339 11.9305 16.3447 11.9648 16.3447 12C16.3447 12.0352 16.3339 12.0695 16.3137 12.0983C16.2936 12.1272 16.2651 12.1491 16.2321 12.1613L14.5596 12.7635C13.6427 13.094 12.81 13.6226 12.1208 14.3118C11.4317 15.0009 10.903 15.8336 10.5726 16.7505L9.97031 18.423C9.9582 18.456 9.93624 18.4845 9.9074 18.5047C9.87856 18.5248 9.84423 18.5356 9.80906 18.5356C9.77388 18.5356 9.73955 18.5248 9.71071 18.5047C9.68188 18.4845 9.65992 18.456 9.64781 18.423L9.04556 16.7505C8.7151 15.8336 8.18645 15.0009 7.49729 14.3118C6.80814 13.6226 5.97544 13.094 5.05856 12.7635L3.38606 12.1613C3.35303 12.1491 3.32452 12.1272 3.30438 12.0983C3.28424 12.0695 3.27344 12.0352 3.27344 12C3.27344 11.9648 3.28424 11.9305 3.30438 11.9017C3.32452 11.8728 3.35303 11.8509 3.38606 11.8388L5.05856 11.2365C5.97544 10.906 6.80814 10.3774 7.49729 9.68824C8.18645 8.99908 8.7151 8.16639 9.04556 7.2495L9.64781 5.577C9.65958 5.54364 9.68141 5.51476 9.71029 5.49432C9.73917 5.47389 9.77368 5.46292 9.80906 5.46292C9.84443 5.46292 9.87894 5.47389 9.90782 5.49432C9.9367 5.51476 9.95853 5.54364 9.97031 5.577ZM17.4988 1.55775L17.8041 2.4045C17.9715 2.86873 18.2393 3.29034 18.5883 3.6393C18.9372 3.98827 19.3588 4.25602 19.8231 4.4235L20.6698 4.72875C20.6866 4.73486 20.7011 4.74598 20.7113 4.76061C20.7216 4.77523 20.727 4.79265 20.727 4.8105C20.727 4.82836 20.7216 4.84578 20.7113 4.8604C20.7011 4.87503 20.6866 4.88615 20.6698 4.89225L19.8231 5.1975C19.3588 5.36498 18.9372 5.63274 18.5883 5.9817C18.2393 6.33067 17.9715 6.75227 17.8041 7.2165L17.4988 8.06325C17.4927 8.08003 17.4816 8.09452 17.467 8.10476C17.4523 8.115 17.4349 8.12049 17.4171 8.12049C17.3992 8.12049 17.3818 8.115 17.3672 8.10476C17.3525 8.09452 17.3414 8.08003 17.3353 8.06325L17.0301 7.2165C16.8626 6.75227 16.5948 6.33067 16.2459 5.9817C15.8969 5.63274 15.4753 5.36498 15.0111 5.1975L14.1643 4.89225C14.1475 4.88615 14.133 4.87503 14.1228 4.8604C14.1126 4.84578 14.1071 4.82836 14.1071 4.8105C14.1071 4.79265 14.1126 4.77523 14.1228 4.76061C14.133 4.74598 14.1475 4.73486 14.1643 4.72875L15.0111 4.4235C15.4753 4.25602 15.8969 3.98827 16.2459 3.6393C16.5948 3.29034 16.8626 2.86873 17.0301 2.4045L17.3353 1.55775C17.3414 1.54098 17.3525 1.52649 17.3672 1.51625C17.3818 1.50601 17.3992 1.50052 17.4171 1.50052C17.4349 1.50052 17.4523 1.50601 17.467 1.51625C17.4816 1.52649 17.4927 1.54098 17.4988 1.55775ZM17.4988 15.9375L17.8041 16.7843C17.9715 17.2485 18.2393 17.6701 18.5883 18.0191C18.9372 18.368 19.3588 18.6358 19.8231 18.8033L20.6698 19.1085C20.6866 19.1146 20.7011 19.1257 20.7113 19.1404C20.7216 19.155 20.727 19.1724 20.727 19.1903C20.727 19.2081 20.7216 19.2255 20.7113 19.2402C20.7011 19.2548 20.6866 19.2659 20.6698 19.272L19.8231 19.5773C19.3588 19.7447 18.9372 20.0125 18.5883 20.3615C18.2393 20.7104 17.9715 21.132 17.8041 21.5963L17.4988 22.443C17.4927 22.4598 17.4816 22.4743 17.467 22.4845C17.4523 22.4947 17.4349 22.5002 17.4171 22.5002C17.3992 22.5002 17.3818 22.4947 17.3672 22.4845C17.3525 22.4743 17.3414 22.4598 17.3353 22.443L17.0301 21.5963C16.8626 21.132 16.5948 20.7104 16.2459 20.3615C15.8969 20.0125 15.4753 19.7447 15.0111 19.5773L14.1643 19.272C14.1475 19.2659 14.133 19.2548 14.1228 19.2402C14.1126 19.2255 14.1071 19.2081 14.1071 19.1903C14.1071 19.1724 14.1126 19.155 14.1228 19.1404C14.133 19.1257 14.1475 19.1146 14.1643 19.1085L15.0111 18.8033C15.4753 18.6358 15.8969 18.368 16.2459 18.0191C16.5948 17.6701 16.8626 17.2485 17.0301 16.7843L17.3353 15.9375C17.3631 15.861 17.4718 15.861 17.4988 15.9375Z" fill="#9A9AB7"/>
		</svg>
	);

	const aiIcon = (
		<svg width="18" height="21" viewBox="0 0 18 21" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path d="M6.69687 4.07649L7.29912 5.74899C7.62958 6.66587 8.15823 7.49856 8.84738 8.18772C9.53654 8.87688 10.3692 9.40553 11.2861 9.73598L12.9586 10.3382C12.9916 10.3503 13.0202 10.3723 13.0403 10.4011C13.0604 10.43 13.0712 10.4643 13.0712 10.4995C13.0712 10.5347 13.0604 10.569 13.0403 10.5978C13.0202 10.6267 12.9916 10.6486 12.9586 10.6607L11.2861 11.263C10.3692 11.5934 9.53654 12.1221 8.84738 12.8112C8.15823 13.5004 7.62958 14.3331 7.29912 15.25L6.69687 16.9225C6.68476 16.9555 6.6628 16.984 6.63396 17.0042C6.60512 17.0243 6.5708 17.0351 6.53562 17.0351C6.50044 17.0351 6.46612 17.0243 6.43728 17.0042C6.40844 16.984 6.38648 16.9555 6.37437 16.9225L5.77212 15.25C5.44166 14.3331 4.91301 13.5004 4.22386 12.8112C3.5347 12.1221 2.702 11.5934 1.78512 11.263L0.112619 10.6607C0.0795935 10.6486 0.0510832 10.6267 0.030942 10.5978C0.0108007 10.569 0 10.5347 0 10.4995C0 10.4643 0.0108007 10.43 0.030942 10.4011C0.0510832 10.3723 0.0795935 10.3503 0.112619 10.3382L1.78512 9.73598C2.702 9.40553 3.5347 8.87688 4.22386 8.18772C4.91301 7.49856 5.44166 6.66587 5.77212 5.74899L6.37437 4.07649C6.38614 4.04313 6.40798 4.01424 6.43686 3.99381C6.46574 3.97337 6.50024 3.9624 6.53562 3.9624C6.571 3.9624 6.6055 3.97337 6.63438 3.99381C6.66326 4.01424 6.68509 4.04313 6.69687 4.07649ZM14.2254 0.0572353L14.5306 0.903985C14.6981 1.36821 14.9659 1.78982 15.3148 2.13878C15.6638 2.48775 16.0854 2.75551 16.5496 2.92299L17.3964 3.22824C17.4131 3.23434 17.4276 3.24546 17.4379 3.26009C17.4481 3.27471 17.4536 3.29213 17.4536 3.30999C17.4536 3.32784 17.4481 3.34526 17.4379 3.35988C17.4276 3.37451 17.4131 3.38563 17.3964 3.39174L16.5496 3.69699C16.0854 3.86446 15.6638 4.13222 15.3148 4.48119C14.9659 4.83015 14.6981 5.25176 14.5306 5.71599L14.2254 6.56274C14.2193 6.57951 14.2081 6.594 14.1935 6.60424C14.1789 6.61448 14.1615 6.61997 14.1436 6.61997C14.1258 6.61997 14.1083 6.61448 14.0937 6.60424C14.0791 6.594 14.068 6.57951 14.0619 6.56274L13.7566 5.71599C13.5891 5.25176 13.3214 4.83015 12.9724 4.48119C12.6235 4.13222 12.2018 3.86446 11.7376 3.69699L10.8909 3.39174C10.8741 3.38563 10.8596 3.37451 10.8494 3.35988C10.8391 3.34526 10.8336 3.32784 10.8336 3.30999C10.8336 3.29213 10.8391 3.27471 10.8494 3.26009C10.8596 3.24546 10.8741 3.23434 10.8909 3.22824L11.7376 2.92299C12.2018 2.75551 12.6235 2.48775 12.9724 2.13878C13.3214 1.78982 13.5891 1.36821 13.7566 0.903985L14.0619 0.0572353C14.068 0.0404601 14.0791 0.0259701 14.0937 0.0157312C14.1083 0.00549219 14.1258 0 14.1436 0C14.1615 0 14.1789 0.00549219 14.1935 0.0157312C14.2081 0.0259701 14.2193 0.0404601 14.2254 0.0572353ZM14.2254 14.437L14.5306 15.2837C14.6981 15.748 14.9659 16.1696 15.3148 16.5185C15.6638 16.8675 16.0854 17.1353 16.5496 17.3027L17.3964 17.608C17.4131 17.6141 17.4276 17.6252 17.4379 17.6398C17.4481 17.6545 17.4536 17.6719 17.4536 17.6897C17.4536 17.7076 17.4481 17.725 17.4379 17.7396C17.4276 17.7543 17.4131 17.7654 17.3964 17.7715L16.5496 18.0767C16.0854 18.2442 15.6638 18.512 15.3148 18.8609C14.9659 19.2099 14.6981 19.6315 14.5306 20.0957L14.2254 20.9425C14.2193 20.9593 14.2081 20.9738 14.1935 20.984C14.1789 20.9942 14.1615 20.9997 14.1436 20.9997C14.1258 20.9997 14.1083 20.9942 14.0937 20.984C14.0791 20.9738 14.068 20.9593 14.0619 20.9425L13.7566 20.0957C13.5891 19.6315 13.3214 19.2099 12.9724 18.8609C12.6235 18.512 12.2018 18.2442 11.7376 18.0767L10.8909 17.7715C10.8741 17.7654 10.8596 17.7543 10.8494 17.7396C10.8391 17.725 10.8336 17.7076 10.8336 17.6897C10.8336 17.6719 10.8391 17.6545 10.8494 17.6398C10.8596 17.6252 10.8741 17.6141 10.8909 17.608L11.7376 17.3027C12.2018 17.1353 12.6235 16.8675 12.9724 16.5185C13.3214 16.1696 13.5891 15.748 13.7566 15.2837L14.0619 14.437C14.0896 14.3605 14.1984 14.3605 14.2254 14.437Z" fill="#7351FD"/>
		</svg>
	);
	
	const miniAiIcon = (
		<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path d="M18.628 9.37978C19.0105 8.88661 19.2183 8.28032 19.2188 7.65619C19.2188 6.49353 18.522 5.47994 17.4777 5.05463C17.4925 4.93353 17.5 4.81103 17.5 4.68744C17.5 3.05041 16.1683 1.71869 14.5313 1.71869C14.2803 1.71869 14.0347 1.74931 13.7964 1.80994C13.5095 0.93447 12.6897 0.312439 11.7188 0.312439C11.0155 0.312439 10.3905 0.647283 9.99 1.16447C9.59438 0.653533 8.9875 0.317752 8.29844 0.312439C7.65938 0.307752 7.08438 0.571814 6.68281 1.0015C7.3375 1.34213 7.84063 1.90619 8.09844 2.6015C8.1875 2.84369 8.06406 3.114 7.82188 3.20463C7.76875 3.22494 7.7125 3.23431 7.65938 3.23431C7.46875 3.23431 7.28906 3.11713 7.21875 2.92806C7.20313 2.88744 7.1875 2.84681 7.16875 2.80775C6.87656 2.1515 6.20938 1.72338 5.49063 1.71869H5.46875C3.83125 1.71869 2.5 3.04994 2.5 4.68744C2.5 4.81088 2.50781 4.93431 2.52188 5.05463C1.47813 5.47963 0.781251 6.49369 0.781251 7.65619C0.781456 8.28029 0.989237 8.88659 1.37188 9.37963C0.889298 9.94452 0.62442 10.6632 0.625001 11.4062C0.625001 12.8578 1.62031 14.0999 2.99531 14.439C3.08594 15.0718 3.40156 15.6406 3.86563 16.0499C3.94531 15.8515 4.06094 15.6671 4.20469 15.5062C4.28767 15.4139 4.40384 15.3584 4.52773 15.3516C4.65162 15.3449 4.77313 15.3875 4.86563 15.4703C5.05938 15.6437 5.075 15.939 4.90313 16.1328C4.79688 16.2515 4.72188 16.3953 4.6875 16.5484V16.5624C4.6875 17.5062 5.3875 18.289 6.29531 18.4187C6.48125 18.4187 6.6625 18.364 6.81563 18.2609C7.03125 18.1171 7.32188 18.1749 7.46563 18.389C7.60938 18.6046 7.55313 18.8968 7.3375 19.0406C7.21719 19.1203 7.08906 19.1874 6.95469 19.2359C7.43438 19.6031 8.0625 19.7718 8.70781 19.6453C9.23563 19.542 9.68141 19.2448 9.99266 18.839C10.3933 19.3542 11.0172 19.6874 11.7188 19.6874C12.5781 19.6874 13.3522 19.1798 13.7048 18.4184C14.6127 18.2882 15.3125 17.5056 15.3125 16.5624V16.549C15.757 16.3801 16.1483 16.0954 16.4458 15.7245C16.7433 15.3535 16.9362 14.9097 17.0045 14.4392C18.38 14.0993 19.375 12.8581 19.375 11.4062C19.375 10.6571 19.1072 9.94088 18.628 9.37978ZM16.508 13.5781C16.3939 13.5914 16.2886 13.6462 16.2123 13.7321C16.1359 13.8179 16.0937 13.9288 16.0938 14.0437V14.0624C16.0938 14.7546 15.6772 15.3671 15.0642 15.6363C14.9995 15.5205 14.9226 15.4119 14.8348 15.3123C14.7515 15.2212 14.6356 15.1667 14.5123 15.1605C14.389 15.1544 14.2682 15.1972 14.1763 15.2796C14.0843 15.3619 14.0285 15.4773 14.0211 15.6005C14.0137 15.7237 14.0552 15.8449 14.1366 15.9378C14.2904 16.1094 14.3753 16.3319 14.375 16.5624C14.375 17.0795 13.9545 17.4999 13.4375 17.4999C13.2522 17.5003 13.071 17.4455 12.917 17.3424C12.8137 17.274 12.6875 17.2492 12.566 17.2735C12.4445 17.2979 12.3376 17.3693 12.2686 17.4723C12.1996 17.5753 12.1743 17.7013 12.198 17.823C12.2217 17.9446 12.2927 18.0519 12.3953 18.1213C12.4889 18.184 12.5878 18.2367 12.6897 18.281C12.4573 18.5684 12.1047 18.7499 11.7188 18.7499C11.0295 18.7499 10.4688 18.1892 10.4688 17.4999V15.9687C10.4688 15.7124 10.2594 15.5093 10.0047 15.5093H10C8.71719 15.5093 7.475 15.2515 6.30781 14.7437C5.83599 14.5393 5.4599 14.1627 5.25625 13.6906C4.23594 11.3453 4.23594 8.65463 5.25625 6.30775C5.46094 5.83588 5.83594 5.46088 6.30938 5.25619C7.47656 4.74838 8.71875 4.49056 10 4.49056H10.0047C10.2594 4.49056 10.4688 4.28744 10.4688 4.03119V2.49994C10.4688 1.81072 11.0295 1.24994 11.7188 1.24994C11.9966 1.24872 12.2668 1.34063 12.4863 1.51099C12.7057 1.68135 12.8618 1.92033 12.9295 2.18978C12.387 2.53564 11.9722 3.04908 11.7481 3.65213C11.7258 3.71 11.7151 3.77174 11.7168 3.83376C11.7185 3.89578 11.7325 3.95684 11.758 4.01341C11.7835 4.06998 11.8199 4.12093 11.8652 4.16331C11.9105 4.20568 11.9638 4.23865 12.022 4.26028C12.0801 4.28191 12.142 4.29179 12.204 4.28933C12.266 4.28688 12.3269 4.27214 12.3831 4.24597C12.4394 4.21981 12.4899 4.18274 12.5317 4.13691C12.5735 4.09108 12.6058 4.03741 12.6267 3.979C12.8084 3.4889 13.1725 3.08796 13.6428 2.85994L13.6448 2.85869C13.9211 2.72481 14.2242 2.65556 14.5313 2.65619C15.6513 2.65619 16.5625 3.56744 16.5625 4.68744C16.5625 4.879 16.5366 5.06619 16.4855 5.24385C16.4679 5.30506 16.4629 5.36921 16.4709 5.4324C16.4789 5.49558 16.4998 5.55647 16.5321 5.61135C16.5644 5.66623 16.6076 5.71394 16.659 5.75158C16.7104 5.78921 16.7689 5.81598 16.8309 5.83025C17.6713 6.02353 18.2813 6.7915 18.2813 7.65619C18.2813 8.18447 18.0567 8.69056 17.6653 9.04447C17.6158 9.08906 17.5764 9.14369 17.5497 9.20473C17.5231 9.26576 17.5097 9.33179 17.5106 9.39839C17.5115 9.465 17.5266 9.53064 17.5549 9.59095C17.5832 9.65125 17.624 9.70481 17.6747 9.74806C17.9141 9.95293 18.1062 10.2072 18.2379 10.4935C18.3696 10.7797 18.4377 11.0911 18.4375 11.4062C18.4375 12.5156 17.608 13.4493 16.508 13.5781Z" fill="white"/>
			<path d="M8.55273 10.6249H9.25961L8.90617 9.65271L8.55273 10.6249Z" fill="white"/>
			<path d="M13.4424 5.82964C11.2549 4.87808 8.74551 4.87808 6.55801 5.82964C6.22988 5.97183 5.97207 6.22964 5.82988 6.55776C4.87832 8.74526 4.87832 11.2546 5.82988 13.4421C5.97207 13.7703 6.22988 14.0281 6.55801 14.1703C7.65176 14.6468 8.8252 14.8843 10.0002 14.8843C11.1736 14.8843 12.3486 14.6468 13.4424 14.1703C13.7705 14.0281 14.0283 13.7703 14.1705 13.4421C15.1221 11.2546 15.1221 8.74526 14.1705 6.55776C14.0297 6.23099 13.7692 5.97048 13.4424 5.82964ZM10.3166 12.1592C10.2588 12.1803 10.1973 12.1897 10.1358 12.1871C10.0743 12.1844 10.014 12.1696 9.95816 12.1436C9.90238 12.1175 9.85227 12.0807 9.81071 12.0353C9.76916 11.9899 9.73696 11.9367 9.71598 11.8789L9.60082 11.5625H8.21207L8.09691 11.8789C8.05442 11.9957 7.96725 12.0909 7.85458 12.1434C7.74191 12.196 7.61297 12.2017 7.49613 12.1592C7.37929 12.1167 7.28412 12.0295 7.23155 11.9168C7.17898 11.8042 7.17332 11.6752 7.21582 11.5584L8.46582 8.12089C8.49878 8.03052 8.55873 7.95248 8.63755 7.89734C8.71637 7.84221 8.81023 7.81264 8.90642 7.81266C9.00261 7.81267 9.09647 7.84227 9.17526 7.89744C9.25406 7.9526 9.31399 8.03067 9.34691 8.12104L10.5969 11.5585C10.618 11.6164 10.6274 11.6778 10.6248 11.7393C10.6221 11.8008 10.6073 11.8612 10.5812 11.917C10.5552 11.9727 10.5184 12.0228 10.473 12.0644C10.4276 12.106 10.3745 12.1382 10.3166 12.1592ZM12.5002 11.7187C12.5002 11.843 12.4508 11.9622 12.3629 12.0502C12.275 12.1381 12.1558 12.1875 12.0314 12.1875C11.9071 12.1875 11.7879 12.1381 11.7 12.0502C11.6121 11.9622 11.5627 11.843 11.5627 11.7187V8.2812C11.5627 8.15688 11.6121 8.03765 11.7 7.94974C11.7879 7.86184 11.9071 7.81245 12.0314 7.81245C12.1558 7.81245 12.275 7.86184 12.3629 7.94974C12.4508 8.03765 12.5002 8.15688 12.5002 8.2812V11.7187Z" fill="white"/>
		</svg>
	);

	const handleSend = async () => {
		if (input.trim() === '' || isBotTyping) return;

		const newMessage = { text: input.trim(), sender: 'me' };
		setMessages((prev) => [...prev, newMessage]);
		setInput('');
		setIsBotTyping(true);
		setHasSentMessage(true);

		try {
			const response = await sendToAi(newMessage.text);
			setPendingBotMessage({ text: response, sender: 'bot', type: 'normal' });
		} catch (error) {
			const errorMsg = error.message || 'Something went wrong';
			setMessages((prev) => [
				...prev,
				{ text: errorMsg, sender: 'bot', type: 'error' }
			]);
			setIsBotTyping(false);
			setPendingBotMessage(null);
		}
	};

	const sendToAi = async (message) => {
		try {
			const res = await fetch(`${EASYCOMMERCE.rest_base}/ai/analyze`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					email: user?.email || '',
					'X-WP-Nonce': EASYCOMMERCE.nonce,
				},
				body: JSON.stringify({ query: message }),
			});

			const data = await res.json().catch(() => null);

			if (!res.ok) {
				const errorMsg = data?.data?.message || data?.message || 'Network response was not okay';
				throw new Error(errorMsg);
			}

			if (!isLicensed && !usingPersonalAIProvider) {
				EASYCOMMERCE.credits -= 2;
			}
			
			return data?.data?.message || 'No answer found';
		} catch (err) {
			throw new Error(err.message || 'Something went wrong');
		}
	};


	const TypingText = ({ text, speed = 20, onComplete, onType }) => {
		const [displayedText, setDisplayedText] = useState('');

		useEffect(() => {
			let currentIndex = 0;
			setDisplayedText('');

			const intervalId = setInterval(() => {
				setDisplayedText((prev) => prev + text[currentIndex]);
				currentIndex++;

				if (onType) onType();

				if (currentIndex >= text.length) {
					clearInterval(intervalId);
					if (onComplete) onComplete();
				}
			}, speed);
			return () => clearInterval(intervalId);
		}, [text, speed, onComplete, onType]);

		return <div dangerouslySetInnerHTML={{ __html: displayedText }} />;
	};

	const TypingIndicator = () => (
		<div className="mb-4">
			<div className="inline-block max-w-[310px] p-3 rounded-full bg-ec-bg/40 border border-ec-border">
				<div className="flex items-center gap-2">
					<span className="w-2.5 h-2.5 rounded-full typing-dot inline-block"></span>
					<span
						className="w-2.5 h-2.5 rounded-full typing-dot inline-block"
						style={{ animationDelay: '0.12s' }}
					></span>
					<span
						className="w-2.5 h-2.5 rounded-full typing-dot inline-block"
						style={{ animationDelay: '0.24s' }}
					></span>
				</div>
			</div>
		</div>
	);

	return (
		<div className="flex flex-col h-full justify-between font-inter">
			{messages.length === 0 && <AskQuestion />}

			{(messages.length > 0 || isBotTyping) && (
				<>
					<div>
						<div className='bg-[linear-gradient(92deg,#A252FC_-15.48%,#7351FD_42.91%,#C02189_101.3%)] flex justify-center items-center gap-[6px] p-[7px]'>
							{miniAiIcon}
							<h4 className='text-white text-[18px]'>AI Analyst</h4>
						</div>
						<div ref={chatContainerRef} className="easycommerce-ask-ai-container flex flex-col overflow-y-auto max-h-[65vh] p-[24px] thin-scrollbar">
							{messages.map((msg, index) => (
								<div key={index}>
									{msg.sender === 'me' ? (
										<div className="flex flex-col gap-2 mb-[32px] group">
											<div className="max-w-[310px] ml-auto text-sm text-ec-title bg-[#E9EEF6] rounded-[10px] py-2.5 px-5">
												{msg.text}
											</div>
										</div>
									) : msg.type === 'error' ? (
										<ErrorMessage text={msg.text} />
									) : (
										<div>
											{aiIcon}
											<div className="block text-ec-title pt-[5px] pb-2 mb-2 easycommerce-ask-ai-generate-text text-base">
												<div dangerouslySetInnerHTML={{ __html: msg.text }} />
											</div>
										</div>
									)}
								</div>
							))}

							{isBotTyping && !pendingBotMessage && <TypingIndicator />}

							{isBotTyping && pendingBotMessage && (
								<div>
									<div className="block text-ec-title pt-[5px] pb-2 mb-2 easycommerce-ask-ai-generate-text text-base">
										{aiIcon}
										<TypingText
											text={pendingBotMessage.text}
											speed={10}
											onType={scrollToBottom}
											onComplete={() => {
												setMessages((prev) => [...prev, pendingBotMessage]);
												setIsBotTyping(false);
												setPendingBotMessage(null);
											}}
										/>
									</div>
								</div>
							)}
							<div ref={chatEndRef} />
						</div>
					</div>
				</>
				
			)}

			<div>
				<div
					className="px-[14px] py-2 rounded-[20px] ai-search-btn flex items-center justify-between gap-[6px] 
						shadow-[0px_12px_13.2px_0px_#00000014] mb-[24px] mx-[56px] min-h-[52px]"
				>
					{!isFocused && !input && searchIcon}
					<input
						ref={inputRef}
						type="text"
						placeholder="Ask a Question"
						className={`
							border-0 outline-0 shadow-none p-0 grow bg-transparent
							text-[14px] placeholder-[#7A7A99]
							${isBotTyping ? 'cursor-not-allowed opacity-60' : 'cursor-text'}
						`}
						value={input}
						onChange={(e) => setInput(e.target.value)}
						onKeyDown={(e) => e.key === 'Enter' && handleSend()}
						readOnly={isBotTyping}
						onFocus={() => setIsFocused(true)}
						onBlur={() => { if (!input) setIsFocused(false); }}
					/>
					<button
						onClick={handleSend}
						disabled={isBotTyping || !input.trim()}
						aria-label="Send"
						className={`bg-transparent border-0 p-0 transition-colors duration-200
							${input.trim() ? 'text-ec-primary' : 'text-black'}
							disabled:opacity-30`}
					>
						{sendIcon}
					</button>
				</div>

				{EASYCOMMERCE.credits !== '' && (
					<h2 className="text-sm font-normal mx-[56px] mb-4">
						{EASYCOMMERCE.credits > 0 ? (
							<AiCredits usage={2} />
						) : (
							<span className="text-ec-light-black">
								{__('No credits available', 'easycommerce')}
							</span>
						)}
					</h2>
					)
				}
			</div>

			{!hasSentMessage && (
				<ExampleQuestions
					onSelect={(question, resetActive) => {
						setInput(question);
						if (inputRef.current) inputRef.current.focus();
					}}
				/>
			)}
		</div>
	);
};

export default AiChat;
